import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TourPanel, TourOffer } from "@/components/tour/TourPanel";
import { Spotlight } from "@/components/tour/Spotlight";
import { toast } from "sonner";

const TourCtx = createContext({ start: () => {}, offerTour: () => {} });
export const useTour = () => useContext(TourCtx);
const PUBLIC = ["landing", "wall"];
const localKey = (name) => (name === "landing" ? "gv_tour_landing_seen" : `gv_tour_${name}_seen`);
const visitorId = () => {
  let v = localStorage.getItem("gv_visitor");
  if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("gv_visitor", v); }
  return v;
};
const initialLang = () => localStorage.getItem("gv_tour_lang") || ((navigator.language || "").toLowerCase().startsWith("es") ? "es" : "en");

function useVoice() {
  const audio = useRef(null);
  const ctx = useRef(null);
  const analyser = useRef(null);
  const raf = useRef(0);
  const [speaking, setSpeaking] = useState(false);
  const [level, setLevel] = useState(0);
  const unlock = () => {
    if (!audio.current) audio.current = new Audio();
    if (!ctx.current) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        ctx.current = new AC();
        const src = ctx.current.createMediaElementSource(audio.current);
        analyser.current = ctx.current.createAnalyser();
        analyser.current.fftSize = 512;
        src.connect(analyser.current);
        analyser.current.connect(ctx.current.destination);
      } catch { analyser.current = null; }
    }
    ctx.current?.resume?.();
  };
  const loop = () => {
    if (analyser.current) {
      const buf = new Uint8Array(analyser.current.fftSize);
      analyser.current.getByteTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += ((v - 128) / 128) ** 2;
      setLevel(Math.sqrt(sum / buf.length));
    } else setLevel(0.15 + Math.random() * 0.15);
    raf.current = requestAnimationFrame(loop);
  };
  const stop = useCallback(() => {
    cancelAnimationFrame(raf.current);
    if (audio.current) { audio.current.onended = null; audio.current.pause(); }
    setSpeaking(false);
    setLevel(0);
  }, []);
  const play = useCallback((url, onEnd) => {
    stop();
    if (!audio.current) audio.current = new Audio();
    const a = audio.current;
    a.src = url;
    a.onended = () => { stop(); onEnd?.(); };
    a.play().then(() => { setSpeaking(true); loop(); }).catch(() => setSpeaking(false));
  }, [stop]); // eslint-disable-line react-hooks/exhaustive-deps
  return { play, stop, unlock, speaking, level };
}

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, setUser } = useAuth();
  const voice = useVoice();
  const [tour, setTour] = useState(null);
  const [idx, setIdx] = useState(0);
  const [offer, setOffer] = useState(null);
  const [muted, setMuted] = useState(false);
  const [auto, setAuto] = useState(true);
  const [answer, setAnswer] = useState(null);
  const [lang, setLangState] = useState(initialLang);
  const maxSeen = useRef(0);
  const cache = useRef({});
  const timer = useRef(null);
  const autoRef = useRef(true);
  autoRef.current = auto;

  const seen = useCallback((name) => (PUBLIC.includes(name) ? !!localStorage.getItem(localKey(name)) : (user?.tours_seen || []).includes(name)), [user]);
  const markSeen = useCallback((name) => {
    if (PUBLIC.includes(name)) return localStorage.setItem(localKey(name), "1");
    if (!user || seen(name)) return;
    api.post("/tour/seen", { tour: name }).catch(() => {});
    setUser((u) => ({ ...u, tours_seen: [...(u.tours_seen || []), name] }));
  }, [user, seen, setUser]);

  const track = useCallback((t, action, step = 0, route = "") => {
    api.post("/tour/event", { tour: t, action, step, lang, visitor: visitorId(), route }).catch(() => {});
  }, [lang]);

  const audioUrl = useCallback(async (name, i, lg) => {
    const k = `${name}:${i}:${lg}`;
    if (!cache.current[k]) cache.current[k] = api.get(`/tour/${name}/audio/${i}`, { params: { lang: lg }, responseType: "blob" }).then((r) => URL.createObjectURL(r.data)).catch(() => { delete cache.current[k]; return null; });
    return cache.current[k];
  }, []);

  const load = useCallback(async (name, lg, path) => {
    const { data } = await api.get(`/tour/${name}`, { params: { lang: lg } });
    if (name !== "help") return { ...data, map: data.steps.map((_, i) => i) };
    const i = data.routes.findIndex((r) => matchPath(r, path));
    if (i < 0) throw new Error("No help for this page yet");
    return { ...data, steps: [data.steps[i]], map: [i], route: data.routes[i] };
  }, []);

  const start = useCallback(async (name, from = 0) => {
    voice.unlock();
    setOffer(null);
    try {
      const data = await load(name, lang, pathname);
      const first = Math.min(Math.max(0, Number(from) || 0), data.steps.length - 1);
      maxSeen.current = first;
      setAnswer(null); setIdx(first); setAuto(true); setTour(data);
      if (name === "help") track("help", "help", data.map[0], data.route);
      else { track(name, "start"); markSeen(name); }
    } catch (e) { toast.error(e.response ? formatErr(e) : e.message); }
  }, [voice, markSeen, load, lang, pathname, track]);

  const setLang = useCallback(async (lg) => {
    setLangState(lg);
    localStorage.setItem("gv_tour_lang", lg);
    if (!tour) return;
    try { const data = await load(tour.name, lg, pathname); setAnswer(null); setTour(data); } catch { /* keep current */ }
  }, [tour, load, pathname]);

  const offerTour = useCallback((name) => { if (!seen(name)) setOffer(name); }, [seen]);
  const dismissOffer = () => { markSeen(offer); setOffer(null); };
  const close = useCallback((finished = false) => {
    if (tour && tour.name !== "help") track(tour.name, finished ? "finish" : "skip", idx);
    clearTimeout(timer.current); voice.stop(); setTour(null); setAnswer(null);
  }, [voice, tour, idx, track]);
  const go = useCallback((i) => { clearTimeout(timer.current); voice.unlock(); setAnswer(null); setIdx(i); }, [voice]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const name = q.get("resume_tour");
    if (!name || !user) return;
    const from = q.get("step");
    q.delete("resume_tour"); q.delete("step");
    window.history.replaceState(null, "", `${window.location.pathname}${q.toString() ? `?${q}` : ""}`);
    setOffer(null);
    setTimeout(() => start(name, from), 800);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = tour?.steps[idx];
  useEffect(() => {
    if (!tour) return;
    if (step.route && step.route !== pathname) navigate(step.route);
    if (tour.name !== "help") {
      track(tour.name, idx < maxSeen.current || (idx === 0 && maxSeen.current > 0) ? "replay" : "view", idx);
      maxSeen.current = Math.max(maxSeen.current, idx);
    }
  }, [tour?.name, idx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tour || answer) return;
    let live = true;
    voice.stop();
    if (!muted) {
      audioUrl(tour.name, tour.map[idx], tour.lang).then((url) => {
        if (!live || !url) return;
        voice.play(url, () => { if (autoRef.current && idx < tour.steps.length - 1) timer.current = setTimeout(() => setIdx((n) => n + 1), 900); });
      });
      if (idx + 1 < tour.steps.length) audioUrl(tour.name, tour.map[idx + 1], tour.lang);
    }
    return () => { live = false; clearTimeout(timer.current); };
  }, [tour, idx, muted, answer]); // eslint-disable-line react-hooks/exhaustive-deps

  const ask = async (question) => {
    voice.unlock();
    clearTimeout(timer.current);
    voice.stop();
    const { data } = await api.post("/tour/ask", { question, tour: tour.name, step: tour.map[idx], lang: tour.lang });
    setAuto(false);
    setAnswer({ question, text: data.answer });
    const j = data.step != null ? tour.map.indexOf(data.step) : -1;
    if (j >= 0 && j !== idx) setIdx(j);
    if (data.audio && !muted) voice.play(`data:audio/mpeg;base64,${data.audio}`);
  };

  const toggleMute = () => { setMuted((m) => { if (!m) voice.stop(); return !m; }); };
  const toggleAuto = () => setAuto((a) => !a);

  return (
    <TourCtx.Provider value={{ start, offerTour, active: !!tour, lang, setLang }}>
      {children}
      {tour && step.target && <Spotlight target={step.target} key={`${tour.name}-${idx}`} />}
      {tour && <TourPanel tour={tour} idx={idx} step={step} answer={answer} voice={voice} muted={muted} auto={auto} lang={tour.lang} onLang={setLang}
        onNext={() => (idx < tour.steps.length - 1 ? go(idx + 1) : close(true))} onBack={() => go(Math.max(0, idx - 1))} onJump={go}
        onClose={() => close(false)} onMute={toggleMute} onAuto={toggleAuto} onAsk={ask} onClearAnswer={() => setAnswer(null)} />}
      {!tour && offer && <TourOffer name={offer} lang={lang} onStart={() => start(offer)} onDismiss={dismissOffer} />}
    </TourCtx.Provider>
  );
}
